import { Megaphone } from 'lucide-react';
import { Button } from '@heroui/react';
import type {
  AiBulkRequestCard,
  AiMisuseCard,
  AiOwnedSpotCard,
  AiUsageCard,
  AiWindowCard,
} from '../../api/ai';
import type { AvailableSpot } from '../../api/available-spots';
import {
  ASSISTANT_AVAILABLE_PERIODS_LABEL,
  ASSISTANT_BULK_REQUEST_HINT,
  ASSISTANT_BULK_REQUEST_LABEL,
  assistantReserveQuestion,
} from './assistant-constants';
import {
  AssistantBulkRequestCards,
  AssistantMisuseCards,
  AssistantOwnedSpotCards,
  AssistantReportMisuseCta,
  AssistantWindowCards,
} from './assistant-feature-cards';
import { availabilityIntroFromMessage, stripChatMarkdown } from './assistant-message';
import { AssistantSpotCards } from './assistant-spot-cards';
import { AssistantUsageCards } from './assistant-usage-cards';

interface AssistantReplyProps {
  content: string;
  spots?: AvailableSpot[];
  usages?: AiUsageCard[];
  ownedSpots?: AiOwnedSpotCard[];
  windows?: AiWindowCard[];
  bulkRequests?: AiBulkRequestCard[];
  misuseReports?: AiMisuseCard[];
  suggestBulkRequest?: boolean;
  suggestReportMisuse?: boolean;
  onReserve: (spot: AvailableSpot) => void;
  onCancelUsage?: (usageId: string) => void;
  canCancelUsage?: (usage: AiUsageCard) => boolean;
  onApproveUsage?: (usageId: string) => void;
  canApproveUsage?: (usage: AiUsageCard) => boolean;
  onRejectUsage?: (usageId: string) => void;
  canRejectUsage?: (usage: AiUsageCard) => boolean;
  onLendSpot?: (spot: AiOwnedSpotCard) => void;
  onCancelWindow?: (windowId: string) => void;
  canCancelWindow?: (window: AiWindowCard) => boolean;
  onCancelBulk?: (requestId: string) => void;
  canCancelBulk?: (request: AiBulkRequestCard) => boolean;
  onWillRemoveMisuse?: (report: AiMisuseCard) => void;
  onAlreadyResolvedMisuse?: (report: AiMisuseCard) => void;
  canResolveMisuse?: (report: AiMisuseCard) => boolean;
  onBulkRequest?: () => void;
  onReportMisuse?: () => void;
}

export function AssistantReply({
  content,
  spots,
  usages,
  ownedSpots,
  windows,
  bulkRequests,
  misuseReports,
  suggestBulkRequest = false,
  suggestReportMisuse = false,
  onReserve,
  onCancelUsage,
  canCancelUsage,
  onApproveUsage,
  canApproveUsage,
  onRejectUsage,
  canRejectUsage,
  onLendSpot,
  onCancelWindow,
  canCancelWindow,
  onCancelBulk,
  canCancelBulk,
  onWillRemoveMisuse,
  onAlreadyResolvedMisuse,
  canResolveMisuse,
  onBulkRequest,
  onReportMisuse,
}: AssistantReplyProps) {
  const bookable = (spots ?? []).filter((spot) => !spot.isMine);
  const requests = usages ?? [];
  const mine = ownedSpots ?? [];
  const publishedWindows = windows ?? [];
  const massRequests = bulkRequests ?? [];
  const misuse = misuseReports ?? [];
  const hasCards =
    bookable.length > 0 ||
    requests.length > 0 ||
    mine.length > 0 ||
    publishedWindows.length > 0 ||
    massRequests.length > 0 ||
    misuse.length > 0 ||
    suggestBulkRequest ||
    suggestReportMisuse;

  if (!hasCards) {
    return <ChatBubble role="assistant" content={stripChatMarkdown(content)} />;
  }

  const intro =
    bookable.length > 0 ||
    requests.length > 0 ||
    mine.length > 0 ||
    publishedWindows.length > 0 ||
    massRequests.length > 0 ||
    misuse.length > 0
      ? availabilityIntroFromMessage(content)
      : stripChatMarkdown(content);

  return (
    <div className="flex justify-start">
      <div className="flex w-full max-w-[90%] flex-col gap-2.5 rounded-2xl bg-surface-secondary px-3.5 py-2.5 text-base leading-relaxed text-foreground">
        {intro ? <p className="whitespace-pre-wrap">{intro}</p> : null}
        {mine.length > 0 ? (
          <AssistantOwnedSpotCards spots={mine} onLend={onLendSpot} embedded />
        ) : null}
        {publishedWindows.length > 0 ? (
          <AssistantWindowCards
            windows={publishedWindows}
            onCancel={onCancelWindow}
            canCancel={canCancelWindow}
            embedded
          />
        ) : null}
        {requests.length > 0 ? (
          <AssistantUsageCards
            usages={requests}
            onCancel={onCancelUsage}
            canCancel={canCancelUsage}
            onApprove={onApproveUsage}
            canApprove={canApproveUsage}
            onReject={onRejectUsage}
            canReject={canRejectUsage}
            embedded
          />
        ) : null}
        {massRequests.length > 0 ? (
          <AssistantBulkRequestCards
            requests={massRequests}
            onCancel={onCancelBulk}
            canCancel={canCancelBulk}
            embedded
          />
        ) : null}
        {misuse.length > 0 ? (
          <AssistantMisuseCards
            reports={misuse}
            onWillRemove={onWillRemoveMisuse}
            onAlreadyResolved={onAlreadyResolvedMisuse}
            canResolve={canResolveMisuse}
            embedded
          />
        ) : null}
        {bookable.length > 0 ? (
          <>
            <p className="text-muted">{ASSISTANT_AVAILABLE_PERIODS_LABEL}</p>
            <AssistantSpotCards spots={bookable} onReserve={onReserve} embedded />
            <p>{assistantReserveQuestion(bookable.length)}</p>
          </>
        ) : null}
        {suggestBulkRequest && onBulkRequest ? (
          <AssistantBulkRequestCta onPress={onBulkRequest} />
        ) : null}
        {suggestReportMisuse && onReportMisuse ? (
          <AssistantReportMisuseCta onPress={onReportMisuse} />
        ) : null}
      </div>
    </div>
  );
}

function AssistantBulkRequestCta({ onPress }: { onPress: () => void }) {
  return (
    <div className="flex flex-col gap-2 border-t border-separator pt-2.5">
      <p className="text-sm text-muted">{ASSISTANT_BULK_REQUEST_HINT}</p>
      <Button size="sm" variant="primary" className="h-8 w-full" onPress={onPress}>
        <Megaphone size={14} />
        {ASSISTANT_BULK_REQUEST_LABEL}
      </Button>
    </div>
  );
}

export function ChatBubble({
  role,
  content,
}: {
  role: 'user' | 'assistant';
  content: string;
}) {
  const isUser = role === 'user';
  const text = isUser ? content : stripChatMarkdown(content);
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <p
        className={`max-w-[90%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-base leading-relaxed ${
          isUser
            ? 'bg-[color-mix(in_oklab,var(--success)_58%,var(--eclipse)_42%)] font-semibold text-white'
            : 'bg-surface-secondary text-foreground'
        }`}
      >
        {text}
      </p>
    </div>
  );
}
